import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Package, 
  Calendar, 
  User, 
  Moon, 
  Sun,
  TrendingUp,
  Sparkles
} from 'lucide-react';
import { useTheme } from '../../hooks/useTheme';
import './MainLayout.css';

const BottomNav = () => {
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();

  const navItems = [
    { to: '/', icon: TrendingUp, label: 'Rates', activeIcon: Sparkles },
    { to: '/products', icon: Package, label: 'Products' },
    { to: '/booking', icon: Calendar, label: 'Book' },
    { to: '/profile', icon: User, label: 'Profile' },
  ];

  return (
    <motion.nav 
      className="bottom-nav"
      initial={{ y: 100 }}
      animate={{ y: 0 }}
      transition={{ type: 'spring', stiffness: 300, damping: 30 }}
    >
      <div className="bottom-nav-inner">
        {navItems.map((item, index) => {
          const isActive = location.pathname === item.to;
          const Icon = item.icon;
          const ActiveIcon = item.activeIcon || item.icon;
          
          return (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) => 
                `bottom-nav-item ${isActive ? 'active' : ''}`
              }
            >
              <motion.div 
                className="bottom-nav-icon-wrapper"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <div className="bottom-nav-icon-container">
                  {isActive ? (
                    <motion.div
                      initial={{ scale: 0, rotate: -180 }}
                      animate={{ scale: 1, rotate: 0 }}
                      transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                    >
                      <ActiveIcon 
                        size={24} 
                        className="bottom-nav-icon active"
                      />
                    </motion.div>
                  ) : (
                    <Icon size={24} className="bottom-nav-icon" />
                  )}
                </div>
                
                {isActive && (
                  <motion.div
                    layoutId="activeBottomIndicator"
                    className="bottom-nav-active-dot"
                    transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                  />
                )}
              </motion.div>
              
              <motion.span 
                className={`bottom-nav-label ${isActive ? 'active' : ''}`}
                animate={{
                  color: isActive ? 'var(--color-primary)' : 'var(--text-muted)',
                  fontWeight: isActive ? 600 : 500
                }}
              >
                {item.label}
              </motion.span>

              {isActive && (
                <motion.div
                  initial={{ scaleX: 0 }}
                  animate={{ scaleX: 1 }}
                  transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                  className="bottom-nav-active-bar"
                />
              )}
            </NavLink>
          );
        })}

        <motion.button 
          onClick={toggleTheme}
          className="bottom-nav-item theme-toggle-bottom"
          whileHover={{ scale: 1.1, rotate: theme === 'light' ? -15 : 15 }}
          whileTap={{ scale: 0.9 }}
          aria-label="Toggle theme"
        >
          <motion.div 
            className="bottom-nav-icon-wrapper"
            animate={{
              rotate: theme === 'dark' ? 360 : 0
            }}
            transition={{ duration: 0.5 }}
          >
            <div className="bottom-nav-icon-container">
              {theme === 'light' ? (
                <Moon size={24} className="bottom-nav-icon" />
              ) : (
                <Sun size={24} className="bottom-nav-icon" />
              )}
            </div>
          </motion.div>
          <motion.span 
            className="bottom-nav-label"
            animate={{
              color: theme === 'dark' ? 'var(--color-primary)' : 'var(--text-muted)'
            }}
          >
            {theme === 'light' ? 'Dark' : 'Light'}
          </motion.span>
        </motion.button>
      </div>
    </motion.nav>
  );
};

export default BottomNav;